import { useRef, useState } from 'react';
import { Badge, Card, ErrorMessage, Field, Loading, Modal, useAsync, useToast } from '../components/ui';
import { RowActions } from '../components/RowActions';
import { IconAlert, IconCheck, IconDownload, IconPlus, IconUpload } from '../components/icons';
import { api, downloadFile, request } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/format';

const ABSENCE_TYPES: Record<string, string> = {
  urlaub: 'Urlaub',
  schulung: 'Schulung',
  sonstiges: 'Sonstiges',
};

/**
 * Eigene Abwesenheiten (Kap. 6) – jede Person pflegt ihre eigenen Einträge
 * selbst, ohne das Recht „Abwesenheiten pflegen“ zu benötigen. Das Backend
 * lässt das für die eigene Kennung ausdrücklich zu (`canManageAbsence` in
 * `routes/catalogs.ts`), wirkt sofort und ohne Freigabeschritt – Wirksam wird
 * ein Eintrag mit dem Speichern, wie beim Admin-Formular auch.
 */
export function MyAbsences() {
  const { user } = useAuth();
  const { notify } = useToast();
  const absences = useAsync(() => api.get('/api/catalogs/absences').catch(() => []), []);
  const subscription = useAsync(() => api.get('/api/catalogs/absences/subscription').catch(() => null), []);
  const colleagues = useAsync(() => api.get('/api/users').catch(() => []), []);

  const [modal, setModal] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const colleagueList = colleagues.data?.items ?? colleagues.data ?? [];

  const saveSubscription = async () => {
    if (!icalUrl.trim()) return;
    setSubscriptionBusy(true);
    setError(null);
    try {
      const result = await api.put('/api/catalogs/absences/subscription', { icalUrl: icalUrl.trim() });
      if (result.ok) {
        notify(`Kalender abonniert, ${result.count ?? 0} Termin(e) übernommen.`, 'success');
        setIcalUrl('');
        subscription.reload();
        absences.reload();
      } else {
        notify(`Der Kalender ließ sich nicht abrufen: ${result.error ?? 'unbekannter Fehler'}`, 'error');
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setSubscriptionBusy(false);
    }
  };

  const removeSubscription = async () => {
    if (!window.confirm('Urlaubskalender-Abo entfernen? Die daraus übernommenen Urlaubstage werden mit entfernt.')) return;
    await request('/api/catalogs/absences/subscription', { method: 'DELETE' });
    subscription.reload();
    absences.reload();
    notify('Abo entfernt.', 'success');
  };

  return (
    <div className="stack">
      <ErrorMessage error={error} />

      <Card
        title="Meine Abwesenheiten"
        subtitle="Wirkt sofort in der Disposition – ohne besonderes Recht, ohne Freigabeschritt"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="btn sm" onClick={() => setImportOpen(true)}>
              <IconUpload width={14} height={14} /> .ical importieren
            </button>
            <button className="btn primary sm" onClick={() => setModal('new')}>
              <IconPlus width={14} height={14} /> Abwesenheit erfassen
            </button>
          </div>
        }
        noBody
      >
        {absences.loading ? (
          <Loading />
        ) : (absences.data ?? []).length === 0 ? (
          <div className="card-body">
            <span className="small subtle">Noch keine Abwesenheit erfasst.</span>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Grund</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th>Vertretung</th>
                  <th>Bemerkung</th>
                  <th>Herkunft</th>
                  <th className="actions">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {(absences.data ?? []).map((entry: any) => (
                  <tr key={entry.id}>
                    <td>
                      <Badge variant="info">
                        {ABSENCE_TYPES[entry.type] ?? entry.type}
                      </Badge>
                    </td>
                    <td className="small">{formatDate(entry.start_date)}</td>
                    <td className="small">{formatDate(entry.end_date)}</td>
                    <td className="small subtle">
                      {entry.sub_first_name ? `${entry.sub_first_name} ${entry.sub_last_name}` : '–'}
                    </td>
                    <td className="small subtle truncate" style={{ maxWidth: 200 }}>
                      {entry.note ?? '–'}
                    </td>
                    <td className="small subtle">
                      {entry.source === 'ical_subscription'
                        ? 'Kalender-Abo'
                        : entry.source === 'ical_import'
                          ? '.ical-Import'
                          : 'von Hand'}
                    </td>
                    <td className="actions">
                      <RowActions
                        label={ABSENCE_TYPES[entry.type] ?? entry.type}
                        onEdit={() => setModal(`edit-${entry.id}`)}
                        deletePath={`/api/catalogs/absences/${entry.id}`}
                        confirmText="Diese Abwesenheit löschen?"
                        onDeleted={() => absences.reload()}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {subscription.data?.disabled ? null : (
      <Card
        title="Urlaubskalender-Abo"
        subtitle="Individuell – ein persönlicher Kalender (z. B. privates Google- oder Outlook-Konto), der stündlich abgeglichen wird"
      >
        <div className="stack">
          {subscription.loading ? (
            <Loading />
          ) : subscription.data ? (
            <div className="stack" style={{ gap: 8 }}>
              <div className="detail-item">
                <span className="detail-label">Abonnierte Adresse</span>
                <span className="detail-value mono small">{subscription.data.ical_url}</span>
              </div>
              <div className="row" style={{ gap: 16 }}>
                <span className="small subtle">
                  {subscription.data.last_synced_at
                    ? `Zuletzt abgeglichen: ${formatDateTime(subscription.data.last_synced_at)}`
                    : 'Noch nicht abgeglichen.'}
                </span>
                {subscription.data.last_error ? (
                  <Badge variant="danger">
                    <IconAlert width={12} height={12} /> {subscription.data.last_error}
                  </Badge>
                ) : (
                  <Badge variant="success">
                    <IconCheck width={12} height={12} /> in Ordnung
                  </Badge>
                )}
              </div>
              <div className="row">
                <button className="btn danger sm" onClick={removeSubscription}>
                  Abo entfernen
                </button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <input
                type="url"
                placeholder="https://calendar.google.com/…/basic.ics"
                value={icalUrl}
                onChange={(event) => setIcalUrl(event.target.value)}
                style={{ minWidth: 340 }}
              />
              <button className="btn primary" disabled={subscriptionBusy || !icalUrl.trim()} onClick={saveSubscription}>
                {subscriptionBusy ? 'Wird abgeglichen …' : 'Kalender abonnieren'}
              </button>
            </div>
          )}
        </div>
      </Card>
      )}

      {(modal === 'new' || modal?.startsWith('edit-')) && (
        <AbsenceSelfModal
          absence={(absences.data ?? []).find((entry: any) => `edit-${entry.id}` === modal)}
          userId={user!.id}
          colleagues={colleagueList}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            absences.reload();
            notify('Abwesenheit gespeichert.', 'success');
          }}
        />
      )}

      {importOpen && (
        <IcalImportModal
          userId={user!.id}
          onClose={() => setImportOpen(false)}
          onImported={() => {
            setImportOpen(false);
            absences.reload();
          }}
        />
      )}
    </div>
  );
}

function AbsenceSelfModal({
  absence,
  userId,
  colleagues,
  onClose,
  onSaved,
}: {
  absence?: any;
  userId: number;
  colleagues: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    type: absence?.type ?? 'urlaub',
    startDate: absence?.start_date ?? '',
    endDate: absence?.end_date ?? '',
    substituteUserId: absence?.substitute_user_id ? String(absence.substitute_user_id) : '',
    note: absence?.note ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  const invalidRange = Boolean(form.startDate && form.endDate && form.endDate < form.startDate);

  return (
    <Modal
      title={absence ? 'Abwesenheit bearbeiten' : 'Abwesenheit erfassen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.startDate || !form.endDate || invalidRange}
            onClick={async () => {
              try {
                const payload = {
                  userId,
                  type: form.type,
                  startDate: form.startDate,
                  endDate: form.endDate,
                  substituteUserId: form.substituteUserId ? Number(form.substituteUserId) : null,
                  note: form.note || null,
                };
                if (absence) await api.patch(`/api/catalogs/absences/${absence.id}`, payload);
                else await api.post('/api/catalogs/absences', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Grund" required>
            <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
              {Object.entries(ABSENCE_TYPES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Von" required>
            <input type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
          </Field>
          <Field label="Bis" required>
            <input type="date" value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
          </Field>
        </div>
        {invalidRange && <div className="alert warning">Das Enddatum liegt vor dem Startdatum.</div>}
        <Field label="Vertretung" hint="Übernimmt in dieser Zeit Ihre Zuweisungen.">
          <select
            value={form.substituteUserId}
            onChange={(event) => setForm({ ...form, substituteUserId: event.target.value })}
          >
            <option value="">Keine Vertretung</option>
            {colleagues
              .filter((person: any) => person.id !== userId)
              .map((person: any) => (
                <option key={person.id} value={person.id}>
                  {person.first_name} {person.last_name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

/** Import einer .ical-Datei – Vorschau vor Übernahme, wie beim Artikelimport. */
function IcalImportModal({
  userId,
  onClose,
  onImported,
}: {
  userId: number;
  onClose: () => void;
  onImported: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState<any[] | null>(null);
  const [selection, setSelection] = useState<Record<string, { include: boolean; type: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const readFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = String(reader.result).split(',')[1] ?? '';
      setContent(base64);
      setBusy(true);
      setError(null);
      try {
        const result = await api.post('/api/catalogs/absences/import/preview', {
          fileName: file.name,
          content: base64,
          userId,
        });
        setPreview(result.events);
        const nextSelection: Record<string, { include: boolean; type: string }> = {};
        for (const event of result.events) nextSelection[event.uid] = { include: true, type: event.suggestedType };
        setSelection(nextSelection);
      } catch (caught) {
        setError(caught);
      } finally {
        setBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const runImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const selections = Object.entries(selection)
        .filter(([, value]) => value.include)
        .map(([uid, value]) => ({ uid, type: value.type }));
      const result = await api.post('/api/catalogs/absences/import', { fileName, content, userId, selections });
      onImported();
      if (result.skipped > 0) {
        // eslint-disable-next-line no-alert
        window.alert(`${result.imported} übernommen, ${result.skipped} übersprungen (bereits vorhanden).`);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Abwesenheiten aus .ical-Datei importieren"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          {preview && (
            <button
              className="btn primary"
              disabled={busy || !Object.values(selection).some((entry) => entry.include)}
              onClick={runImport}
            >
              {busy ? 'Wird übernommen …' : 'Übernehmen'}
            </button>
          )}
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        {!preview ? (
          <div className="stack">
            <label className="btn" style={{ cursor: 'pointer', width: 'fit-content' }}>
              <IconUpload width={14} height={14} /> {busy ? 'Wird gelesen …' : 'Datei wählen …'}
              <input
                ref={fileInput}
                type="file"
                accept=".ics,text/calendar"
                style={{ display: 'none' }}
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) readFile(file);
                }}
              />
            </label>
            <div className="note-box tone-info">
              <span>
                Die Musterdatei enthält zwei Termine und zeigt zugleich, woran der Typ erkannt wird – „Urlaub“
                und „Schulung“ im Titel.
              </span>
              <button
                className="btn sm"
                onClick={() =>
                  downloadFile('/api/catalogs/absences/import/muster.ics', 'abwesenheiten-musterdatei.ics').catch(
                    () => setError(new Error('Die Musterdatei konnte nicht geladen werden.')),
                  )
                }
              >
                <IconDownload width={14} height={14} /> Musterdatei
              </button>
            </div>
          </div>
        ) : preview.length === 0 ? (
          <span className="small subtle">Die Datei enthält keine Termine.</span>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th />
                  <th>Titel</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th>Übernehmen als</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((event: any) => (
                  <tr key={event.uid}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selection[event.uid]?.include ?? true}
                        onChange={(evt) =>
                          setSelection((current) => ({
                            ...current,
                            [event.uid]: { ...current[event.uid], include: evt.target.checked },
                          }))
                        }
                      />
                    </td>
                    <td className="small">{event.summary || '(ohne Titel)'}</td>
                    <td className="small">{formatDate(event.startDate)}</td>
                    <td className="small">{formatDate(event.endDate)}</td>
                    <td>
                      <select
                        value={selection[event.uid]?.type ?? 'urlaub'}
                        onChange={(evt) =>
                          setSelection((current) => ({
                            ...current,
                            [event.uid]: { ...current[event.uid], type: evt.target.value },
                          }))
                        }
                      >
                        {Object.entries(ABSENCE_TYPES).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
